import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styles from './BlogManagement.module.css';
import {
  FaPlus, FaCode, FaEye, FaTrash, FaCopy, FaCheck,
  FaSpinner, FaFileUpload, FaChevronLeft, FaChevronRight,
  FaMagic, FaCloudUploadAlt, FaHistory, FaCheckCircle,
  FaExclamationTriangle, FaChartLine, FaBrain, FaSlidersH,
  FaSyncAlt, FaKey, FaShieldAlt, FaCalendarAlt, FaTimesCircle, FaClock
} from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../../config';

export default function BlogManagement() {
  const [blogs, setBlogs] = useState([]);
  const [view, setView] = useState('dashboard'); // 'dashboard', 'editor'

  // Blog Form State
  const [currentBlog, setCurrentBlog] = useState(null);
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [industry, setIndustry] = useState('');
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [keywords, setKeywords] = useState('');
  const [publishedAt, setPublishedAt] = useState('');
  const [status, setStatus] = useState('published'); // draft, published, scheduled

  // Editor State
  const [htmlContent, setHtmlContent] = useState('');
  const [draftSessionId, setDraftSessionId] = useState(() => 'session-' + Date.now() + '-' + Math.random().toString(36).substring(2, 9));
  const visualEditorRef = useRef(null);
  const textareaRef = useRef(null);

  // API Key State
  const [apiKey, setApiKey] = useState('');
  const [apiKeyExpiresAt, setApiKeyExpiresAt] = useState(null);
  const [selectedLang, setSelectedLang] = useState('curl');
  const [isRegeneratingKey, setIsRegeneratingKey] = useState(false);
  const [keyExpiryDays, setKeyExpiryDays] = useState('never'); // 'never', '30', '90', '365'
  const [showRegenerateForm, setShowRegenerateForm] = useState(false);

  // Modal & Visibility State
  const [copiedSnippet, setCopiedSnippet] = useState(false);
  const [copiedApiKey, setCopiedApiKey] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [availableIndustries, setAvailableIndustries] = useState([]);
  const [isCustomIndustry, setIsCustomIndustry] = useState(false);
  const [previewBlog, setPreviewBlog] = useState(null);

  // Layout and SEO Highlight states
  const [layoutMode, setLayoutMode] = useState('split'); // 'split', 'full-visual', 'full-html'
  const [isOptimizingSeo, setIsOptimizingSeo] = useState(false);
  const [seoReport, setSeoReport] = useState(null);
  const [showSeoModal, setShowSeoModal] = useState(false);
  const [highlightedHtml, setHighlightedHtml] = useState('');
  const [isHighlightingChanges, setIsHighlightingChanges] = useState(false);

  // Importing loading state
  const [isImporting, setIsImporting] = useState(false);
  const [importingStep, setImportingStep] = useState(0);

  // Bottom action dialogs (now contain metadata settings)
  const [showPublishConfirmModal, setShowPublishConfirmModal] = useState(false);
  const [showScheduleConfirmModal, setShowScheduleConfirmModal] = useState(false);
  const [showIntegrationModal, setShowIntegrationModal] = useState(false);

  // SEO Brief Generator States
  const [activeBriefTab, setActiveBriefTab] = useState('brief'); // 'brief', 'import'
  const [briefTopic, setBriefTopic] = useState('');
  const [briefTargetAudience, setBriefTargetAudience] = useState('');
  const [briefLanguage, setBriefLanguage] = useState('English');
  const [briefWordCount, setBriefWordCount] = useState('');
  const [briefMandatoryKeywords, setBriefMandatoryKeywords] = useState('');
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [isGeneratingBrief, setIsGeneratingBrief] = useState(false);
  const [generatedBrief, setGeneratedBrief] = useState(null);
  const [isWritingWithAI, setIsWritingWithAI] = useState(false);

  // Floating toolbar for selected image configurations
  const [selectedImg, setSelectedImg] = useState(null);
  const [imgToolbarPos, setImgToolbarPos] = useState({ top: 0, left: 0 });

  const fileInputRef = useRef(null);

  const userData = JSON.parse(localStorage.getItem('authData') || '{}');
  const token = userData?.token || localStorage.getItem('token');

  useEffect(() => {
    fetchBlogs();
    fetchBlogApiKey();
  }, []);

  // Sync visual editor innerHTML to state securely without losing caret focus
  useEffect(() => {
    if (view === 'editor' && visualEditorRef.current) {
      if (document.activeElement !== visualEditorRef.current) {
        if (visualEditorRef.current.innerHTML !== htmlContent) {
          visualEditorRef.current.innerHTML = htmlContent;
        }
      }
    }
  }, [view, htmlContent]);

  const fetchBlogs = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/blogs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setBlogs(res.data.blogs);
        const uniqueIndustries = [...new Set(res.data.blogs.map(b => b.industry))].filter(Boolean);
        setAvailableIndustries(uniqueIndustries);
      }
    } catch (err) {
      console.error('Error fetching blogs:', err);
    }
  };

  const fetchBlogApiKey = async () => {
    if (!token) return;
    try {
      // 1. First attempt to fetch via dedicated blog key info route
      const res = await axios.get(`${API_BASE_URL}/api/blogs/key-info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success && res.data.apiKey) {
        setApiKey(res.data.apiKey);
        setApiKeyExpiresAt(res.data.expiresAt || null);
        return;
      }
    } catch (keyInfoErr) {
      // 2. Fallback to vendorapi/list if dedicated endpoint isn't ready
      try {
        const fallbackRes = await axios.get(`${API_BASE_URL}/api/vendorapi/list`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (fallbackRes.data.success && Array.isArray(fallbackRes.data.data)) {
          const blogKeyObj = fallbackRes.data.data.find(k => k.name === "Blog API Key");
          if (blogKeyObj && blogKeyObj.apikey !== "Not Set") {
            setApiKey(blogKeyObj.apikey);
            setApiKeyExpiresAt(blogKeyObj.expiresAt || blogKeyObj.blogApiKeyExpiresAt || null);
          }
        }
      } catch (err) {
        console.error('Error fetching API key list:', err);
      }
    }
  };

  const handleGenerateApiKey = async (expiryOption = keyExpiryDays) => {
    if (!token) {
      toast.error("You are not authenticated.");
      return;
    }
    setIsRegeneratingKey(true);
    try {
      const expiresInDays = expiryOption === 'never' ? null : parseInt(expiryOption, 10);
      const res = await axios.post(
        `${API_BASE_URL}/api/blogs/generate-key`,
        { expiresInDays },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        setApiKey(res.data.apiKey);
        setApiKeyExpiresAt(res.data.expiresAt || null);
        toast.success(
          expiresInDays
            ? `New API Key generated (Valid for ${expiresInDays} days)!`
            : "New Lifetime API Key generated (Never expires)!"
        );
        setShowRegenerateForm(false);
      } else {
        toast.error(res.data.message || "Failed to generate API Key.");
      }
    } catch (err) {
      console.error("Error generating API key:", err);
      toast.error(err.response?.data?.message || "Failed to generate API Key.");
    } finally {
      setIsRegeneratingKey(false);
    }
  };

  const getApiKeyExpiryStatus = () => {
    if (!apiKey) return null;
    if (!apiKeyExpiresAt) {
      return {
        isExpired: false,
        status: 'lifetime',
        label: 'Never Expires (Lifetime Key)',
        color: '#15803d',
        bgColor: '#dcfce7',
        borderColor: '#86efac'
      };
    }

    const expiryDate = new Date(apiKeyExpiresAt);
    const now = new Date();
    const isExpired = now > expiryDate;
    const formattedDate = expiryDate.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    if (isExpired) {
      return {
        isExpired: true,
        status: 'expired',
        label: `Expired on ${formattedDate}`,
        subLabel: 'This API key has expired. External requests are blocked until a new key is generated.',
        color: '#b91c1c',
        bgColor: '#fee2e2',
        borderColor: '#fca5a5'
      };
    }

    const diffDays = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
    return {
      isExpired: false,
      status: 'active',
      label: `Expires on ${formattedDate} (${diffDays} day${diffDays === 1 ? '' : 's'} remaining)`,
      color: '#b45309',
      bgColor: '#fef3c7',
      borderColor: '#fde68a'
    };
  };

  const handleCreateNew = () => {
    setCurrentBlog(null);
    setDraftSessionId('session-' + Date.now() + '-' + Math.random().toString(36).substring(2, 9));
    setTitle('');
    setSlug('');
    setIndustry(availableIndustries[0] || '');
    setIsCustomIndustry(availableIndustries.length === 0);
    setThumbnailUrl('');
    setMetaTitle('');
    setMetaDescription('');
    setKeywords('');
    setPublishedAt('');
    setStatus('published');
    setHtmlContent('');
    setLayoutMode('split');
    
    // Reset Brief generator states
    setBriefTopic('');
    setBriefTargetAudience('');
    setBriefLanguage('English');
    setBriefWordCount('');
    setBriefMandatoryKeywords('');
    setIsAdvancedOpen(false);
    setGeneratedBrief(null);
    setActiveBriefTab('brief');
    
    setView('brief');
  };

  const handleEdit = (blog) => {
    setCurrentBlog(blog);
    setTitle(blog.title);
    setSlug(blog.slug);
    setIndustry(blog.industry);
    setIsCustomIndustry(!availableIndustries.includes(blog.industry));
    setThumbnailUrl(blog.thumbnailUrl || '');
    setMetaTitle(blog.metaTitle || '');
    setMetaDescription(blog.metaDescription || '');
    setKeywords(blog.keywords?.join(', ') || '');
    setPublishedAt(blog.publishedAt ? new Date(blog.publishedAt).toISOString().slice(0, 16) : '');
    setStatus(blog.status);
    setHtmlContent(blog.htmlContent);
    setLayoutMode('split');
    setView('editor');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this blog? All associated cloud images for this blog post will also be deleted permanently.')) return;
    try {
      const res = await axios.delete(`${API_BASE_URL}/api/blogs/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        toast.success(res.data.message || "Blog and associated images deleted successfully!");
        fetchBlogs();
      } else {
        toast.error(res.data.message || "Failed to delete blog.");
      }
    } catch (err) {
      console.error("Delete error:", err);
      toast.error(err.response?.data?.message || 'Failed to delete blog');
    }
  };

  // Helper: Detects and uploads Base64 embedded images in editor html to S3.
  // This keeps MongoDB document sizes small and avoids database payload crashes.
  const processBase64Images = async (content) => {
    if (!content || typeof content !== 'string') return content;
    // Regex matches the pattern src="data:image/png;base64,iVBORw0KG..." with single/double quotes and all image types
    const base64Regex = /src=["'](data:(image\/[a-zA-Z0-9\+\-\.]+);(?:charset=[^;,]+;)?base64,([^"'\s>]+))["']/gi;
    let matches;
    let updatedContent = content;
    
    const uploads = [];
    // Reset regex index and find all matches in content
    base64Regex.lastIndex = 0;
    while ((matches = base64Regex.exec(content)) !== null) {
      const fullDataUrl = matches[1];
      const mimeType = matches[2];
      const base64Data = matches[3];
      if (!uploads.some(u => u.fullDataUrl === fullDataUrl)) {
        uploads.push({ fullDataUrl, mimeType, base64Data });
      }
    }

    if (uploads.length === 0) return content;

    const toastId = toast.loading(`Uploading ${uploads.length} embedded images to cloud storage...`);
    
    try {
      for (let i = 0; i < uploads.length; i++) {
        const { fullDataUrl, mimeType, base64Data } = uploads[i];
        
        // Convert Base64 data string to binary bytes
        const cleanBase64 = base64Data.replace(/\s+/g, '');
        const byteCharacters = atob(cleanBase64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let j = 0; j < byteCharacters.length; j++) {
          byteNumbers[j] = byteCharacters.charCodeAt(j);
        }
        const byteArray = new Uint8Array(byteNumbers);
        
        // Wrap binary bytes in a File/Blob format for S3 uploads
        const blob = new Blob([byteArray], { type: mimeType });
        let extension = mimeType.split('/')[1] || 'png';
        if (extension === 'jpeg') extension = 'jpg';
        if (extension.includes('svg')) extension = 'svg';
        const file = new File([blob], `embedded-image-${Date.now()}-${i}.${extension}`, { type: mimeType });

        // Prepare multi-part request payload
        const formData = new FormData();
        formData.append('image', file);
        if (currentBlog?._id) {
          formData.append('blogId', currentBlog._id);
        } else {
          formData.append('draftSessionId', draftSessionId);
        }

        const res = await axios.post(`${API_BASE_URL}/api/blogs/upload-image`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`
          }
        });

        if (res.data.success && res.data.url) {
          // Replace base64 URL with permanent S3 URL in HTML content
          updatedContent = updatedContent.split(fullDataUrl).join(res.data.url);
        } else {
          throw new Error(res.data.message || "Upload failed");
        }
      }
      toast.success("Embedded images uploaded to S3 successfully!");
    } catch (err) {
      console.error("❌ Base64 image auto-upload failed:", err);
      toast.error("Failed to upload copy-pasted images to cloud storage. Saving text only.");
    } finally {
      toast.dismiss(toastId);
    }

    return updatedContent;
  };

  const handleSave = async (saveStatus) => {
    if (!title.trim() || !htmlContent.trim()) {
      toast.error("Please provide at least a Blog Title and Content before saving.");
      return;
    }

    setIsSaving(saveStatus || status || 'save');
    try {
      // 1. Process any copy-pasted Base64 data images first to prevent DB payload limit issues
      const cleanedHtmlContent = await processBase64Images(htmlContent);

      const payload = {
        title,
        slug,
        industry: industry || 'General',
        thumbnailUrl,
        metaTitle,
        metaDescription,
        keywords: keywords.split(',').map(k => k.trim()).filter(k => k),
        publishedAt: publishedAt || Date.now(),
        status: saveStatus || status,
        htmlContent: cleanedHtmlContent,
        draftSessionId
      };

      if (currentBlog) {
        await axios.put(`${API_BASE_URL}/api/blogs/${currentBlog._id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Blog updated successfully!");
      } else {
        await axios.post(`${API_BASE_URL}/api/blogs`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Blog created successfully!");
      }

      fetchBlogs();
      setView('dashboard');
    } catch (err) {
      toast.error(err.response?.data?.error || err.response?.data?.message || 'Failed to save blog');
    } finally {
      setIsSaving(false);
    }
  };

  // Drag & drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      processFile(file);
    }
  };

  const processFile = (file) => {
    if (!file) return;
    setIsImporting(true);
    setImportingStep(0);

    // Elegant Multi-step Loading text carousel
    const steps = [
      "Reading document contents...",
      "Extracting HTML headers & structure...",
      "Analyzing body formatting & tags...",
      "Compiling layout parameters...",
      "Writing SEO drafts...",
      "Almost ready..."
    ];

    let currentStep = 0;
    const interval = setInterval(() => {
      currentStep++;
      if (currentStep < steps.length) {
        setImportingStep(currentStep);
      } else {
        clearInterval(interval);

        const reader = new FileReader();
        reader.onload = async (evt) => {
          const result = evt.target.result;

          // Parse HTML and automatically extract first h1 tag text as title!
          try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(result, "text/html");
            const h1Element = doc.querySelector("h1");
            if (h1Element && h1Element.textContent) {
              const extractedTitle = h1Element.textContent.trim();
              setTitle(extractedTitle);
              setMetaTitle(extractedTitle);

              // Generate URL slug from title
              const generatedSlug = extractedTitle
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-")
                .replace(/(^-|-$)/g, "");
              setSlug(generatedSlug);
            }
          } catch (e) {
            console.error("Error parsing title from imported HTML:", e);
          }

          // Auto-convert any Base64 binary images to hosted S3 URLs
          let finalHtml = result;
          if (finalHtml && finalHtml.includes('data:image/')) {
            finalHtml = await processBase64Images(result);
          }
          setHtmlContent(finalHtml);

          setIsImporting(false);
          toast.success("Document imported successfully!");
          setView('editor');
        };
        reader.readAsText(file);
      }
    }, 500);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) processFile(file);
    e.target.value = ''; // Reset input
  };

  const handleCopySnippet = () => {
    navigator.clipboard.writeText(getSnippet());
    setCopiedSnippet(true);
    setTimeout(() => setCopiedSnippet(false), 2000);
  };

  const handleCopyApiKey = () => {
    if (!apiKey) return;
    navigator.clipboard.writeText(apiKey);
    setCopiedApiKey(true);
    setTimeout(() => setCopiedApiKey(false), 2000);
  };

  const handleVisualEdit = (e) => {
    if (e && e.currentTarget) {
      setHtmlContent(e.currentTarget.innerHTML);
    } else if (visualEditorRef.current) {
      setHtmlContent(visualEditorRef.current.innerHTML);
    }
  };

  // Generate SEO Brief from User Topic & Inputs
  const handleGenerateBrief = async () => {
    if (!briefTopic.trim()) {
      toast.error("Please enter a topic or prompt description.");
      return;
    }
    setIsGeneratingBrief(true);
    if (!generatedBrief) {
      setGeneratedBrief(null);
    }
    try {
      const payload = { topic: briefTopic.trim() };
      
      if (briefTargetAudience.trim()) {
        payload.targetAudience = briefTargetAudience.trim();
      }
      if (briefLanguage && briefLanguage !== 'English') {
        payload.language = briefLanguage;
      }
      if (briefWordCount && !isNaN(Number(briefWordCount))) {
        payload.wordCountTarget = Number(briefWordCount);
      }
      if (briefMandatoryKeywords.trim()) {
        payload.mandatoryKeywords = briefMandatoryKeywords
          .split(',')
          .map(k => k.trim())
          .filter(k => k);
      }

      const res = await axios.post(`${API_BASE_URL}/api/blogs/generate-brief`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data.success) {
        setGeneratedBrief(res.data.brief);
        toast.success("SEO Brief generated successfully!");
      } else {
        toast.error("Brief generation returned an unexpected response format.");
      }
    } catch (err) {
      console.error("Brief generation error:", err);
      toast.error(err.response?.data?.message || "Failed to generate brief. Please check AI credentials.");
    } finally {
      setIsGeneratingBrief(false);
    }
  };

  // Switch to Editor using Brief details
  const handleWriteFromBrief = async (writeMode) => {
    if (!generatedBrief) return;

    // Pre-fill Editor Fields
    const titleVal = generatedBrief.suggestedTitle || "Untitled Blog";
    setTitle(titleVal);
    
    // Generate clean slug from suggested title
    const computedSlug = titleVal
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
    setSlug(computedSlug);

    setMetaTitle(generatedBrief.metaTitleSuggestion || titleVal);
    setMetaDescription(generatedBrief.metaDescriptionSuggestion || '');
    
    const kwList = [];
    if (generatedBrief.primaryKeyword) kwList.push(generatedBrief.primaryKeyword);
    if (Array.isArray(generatedBrief.secondaryKeywords)) kwList.push(...generatedBrief.secondaryKeywords);
    setKeywords(kwList.join(', '));

    if (writeMode === 'ai') {
      setIsWritingWithAI(true);
      try {
        const res = await axios.post(`${API_BASE_URL}/api/blogs/generate-ai`, {
          brief: generatedBrief
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (res.data.success && res.data.blog) {
          const generatedBlog = res.data.blog;
          setHtmlContent(generatedBlog.htmlContent);
          
          if (generatedBlog.title) {
            setTitle(generatedBlog.title);
            const finalSlug = generatedBlog.title
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, "-")
              .replace(/(^-|-$)/g, "");
            setSlug(finalSlug);
          }
          if (generatedBlog.metaTitle) {
            setMetaTitle(generatedBlog.metaTitle);
          }
          if (generatedBlog.metaDescription) {
            setMetaDescription(generatedBlog.metaDescription);
          }
          if (generatedBlog.keywords) {
            const kwStr = Array.isArray(generatedBlog.keywords)
              ? generatedBlog.keywords.join(', ')
              : generatedBlog.keywords;
            setKeywords(kwStr);
          }

          toast.success("AI generated full blog post based on brief!");
          setView('editor');
        } else {
          toast.error("AI blog generation failed. No blog content returned.");
        }
      } catch (err) {
        console.error("AI Blog Write error:", err);
        toast.error(err.response?.data?.message || "Failed to write blog with AI. Please check AI configuration.");
      } finally {
        setIsWritingWithAI(false);
      }
    } else {
      // Manual mode: build skeleton with outline headings
      let initialHtml = `<div style="max-width: 800px; margin: 0 auto; padding: 24px; font-family: 'Inter', sans-serif;">\n`;
      initialHtml += `  <h1 style="font-size: 38px; margin-bottom: 24px; color: #111827;">${titleVal}</h1>\n`;
      
      if (generatedBrief.outline && Array.isArray(generatedBrief.outline.sections)) {
        generatedBrief.outline.sections.forEach(section => {
          initialHtml += `  <h2 style="font-size: 26px; margin-top: 40px; margin-bottom: 16px; color: #1f2937; font-weight: 700; border-left: 5px solid #4f46e5; padding-left: 12px;">${section.h2}</h2>\n`;
          initialHtml += `  <p style="font-size: 16px; line-height: 1.8; color: #4b5563; margin-bottom: 20px;">[Type your content here...]</p>\n`;
          
          if (Array.isArray(section.h3s)) {
            section.h3s.forEach(h3 => {
              initialHtml += `  <h3 style="font-size: 20px; margin-top: 24px; margin-bottom: 12px; color: #374151;">${h3}</h3>\n`;
              initialHtml += `  <p style="font-size: 16px; line-height: 1.8; color: #4b5563; margin-bottom: 20px;">[Type subsection content here...]</p>\n`;
            });
          }
        });
      }
      initialHtml += `</div>`;
      setHtmlContent(initialHtml);
      toast.success("Editor pre-filled with outline headings!");
      setView('editor');
    }
  };

  // Match Visual Selection to Raw HTML Code Textarea
  const handleVisualSelection = () => {
    const selection = window.getSelection();
    if (!selection) return;

    const selectedText = selection.toString().trim();
    if (selectedText.length >= 3) {
      const index = htmlContent.indexOf(selectedText);
      if (index !== -1) {
        if (textareaRef.current) {
          // If HTML text editor is visible
          if (layoutMode === 'split' || layoutMode === 'full-html') {
            textareaRef.current.focus();
            textareaRef.current.setSelectionRange(index, index + selectedText.length);

            // Calculate scroll offset based on character height
            const textBeforeSelection = htmlContent.substring(0, index);
            const lines = textBeforeSelection.split('\n').length;
            textareaRef.current.scrollTop = (lines - 4) * 20; // 20px line-height
          }
        }
      }
    }
  };

  // Async Cloud Image Upload and Insertion Helper
  const uploadAndInsertImage = async (file) => {
    const tempId = `img-upload-${Date.now()}`;
    const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB Client limit

    if (file.size > MAX_FILE_SIZE) {
      toast.error("Image file is too large! Maximum allowed upload size is 20MB.");
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast.error("Only image files (.png, .jpg, .webp, .gif) are supported.");
      return;
    }

    // 1. Insert temporary loading indicator placeholder
    const tempHtml = `<img id="${tempId}" src="https://cdnjs.cloudflare.com/ajax/libs/galleriffic/2.0.1/css/loader.gif" style="max-width: 60px; display: block; margin: 16px auto; opacity: 0.5;" alt="Uploading..." />`;

    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      try {
        const range = selection.getRangeAt(0);
        const fragment = range.createContextualFragment(tempHtml);
        range.insertNode(fragment);
      } catch (err) {
        console.error("Cursor insert error:", err);
      }
    }

    // 2. Perform background upload
    const formData = new FormData();
    formData.append('image', file);
    if (currentBlog?._id) {
      formData.append('blogId', currentBlog._id);
    } else {
      formData.append('draftSessionId', draftSessionId);
    }

    const toastId = toast.loading("Uploading image to cloud storage...");

    try {
      const res = await axios.post(`${API_BASE_URL}/api/blogs/upload-image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        }
      });

      if (res.data.success && res.data.url) {
        // 3. Swap temp image src with permanent S3 URL
        const tempImg = document.getElementById(tempId);
        if (tempImg) {
          tempImg.src = res.data.url;
          tempImg.alt = file.name ? file.name.split('.')[0].replace(/[-_]/g, ' ') : "Blog Image";
          tempImg.style.maxWidth = "100%";
          tempImg.style.height = "auto";
          tempImg.style.opacity = "1";
          tempImg.style.borderRadius = "8px";
          tempImg.style.margin = "16px auto";
          tempImg.style.display = "block";
          tempImg.removeAttribute('id');
          handleVisualEdit();
        }
        toast.dismiss(toastId);
        toast.success("Image uploaded successfully!");
      } else {
        throw new Error(res.data.message || "Upload failed");
      }
    } catch (err) {
      console.error("❌ Image upload failed:", err);
      const errorMsg = err.response?.data?.message || "Failed to upload image to cloud storage.";
      toast.dismiss(toastId);
      toast.error(errorMsg);
      
      const tempImg = document.getElementById(tempId);
      if (tempImg) tempImg.remove();
    }
  };

  // Helper: Upload local image file to S3 as thumbnail URL with validation
  const handleThumbnailUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size limit: 20MB
    const MAX_FILE_SIZE = 20 * 1024 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      toast.error("Image file is too large! Maximum allowed upload size is 20MB.");
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Only image files (.png, .jpg, .webp, .gif) are supported.");
      return;
    }

    const formData = new FormData();
    formData.append('image', file);
    if (currentBlog?._id) {
      formData.append('blogId', currentBlog._id);
    } else {
      formData.append('draftSessionId', draftSessionId);
    }

    const toastId = toast.loading("Uploading thumbnail image to cloud storage...");
    try {
      const res = await axios.post(`${API_BASE_URL}/api/blogs/upload-image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        }
      });

      if (res.data.success && res.data.url) {
        setThumbnailUrl(res.data.url);
        toast.dismiss(toastId);
        toast.success("Thumbnail uploaded successfully!");
      } else {
        throw new Error(res.data.message || "Upload failed");
      }
    } catch (err) {
      console.error("❌ Thumbnail upload failed:", err);
      const errorMsg = err.response?.data?.message || "Failed to upload thumbnail to cloud storage.";
      toast.dismiss(toastId);
      toast.error(errorMsg);
    }
  };

  // Drag and Drop support for local image files inside Visual WYSIWYG Editor
  const handleVisualDrop = async (e) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('image/')) {
        await uploadAndInsertImage(file);
      } else {
        toast.error("Please drop a valid image file.");
      }
    }
  };

  // Copy and Paste support for system clipboard images inside Visual Editor
  const handleVisualPaste = async (e) => {
    const items = (e.clipboardData || e.originalEvent.clipboardData).items;
    for (let index in items) {
      const item = items[index];
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        await uploadAndInsertImage(file);
        break;
      }
    }
  };

  // Helper to insert image HTML at the exact cursor selection range as a standalone block-level node
  const insertImageAtCursor = (base64Url) => {
    const imgHtml = `<img src="${base64Url}" style="max-width: 100%; height: auto; border-radius: 8px; margin: 16px auto; display: block;" alt="Uploaded Image" />`;
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      try {
        const range = selection.getRangeAt(0);
        
        // Find closest block parent node (e.g. P, DIV, H1-H6, LI)
        let startNode = range.startContainer;
        if (startNode.nodeType === Node.TEXT_NODE) {
          startNode = startNode.parentNode;
        }
        
        let blockParent = null;
        let current = startNode;
        while (current && current !== visualEditorRef.current) {
          if (['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'LI', 'BLOCKQUOTE'].includes(current.tagName)) {
            blockParent = current;
            break;
          }
          current = current.parentNode;
        }
        
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = imgHtml;
        const imgElement = tempDiv.firstChild;
        
        if (blockParent && blockParent.parentNode) {
          const contentText = blockParent.textContent.trim();
          const hasBrOnly = blockParent.innerHTML.trim() === '<br>';
          
          // If the block is empty or just has a break line, replace it entirely to keep layout clean
          if (contentText === '' || hasBrOnly) {
            blockParent.parentNode.replaceChild(imgElement, blockParent);
          } else {
            // Otherwise insert it directly after the block element (sibling)
            blockParent.parentNode.insertBefore(imgElement, blockParent.nextSibling);
          }
        } else {
          // Fallback if no block parent exists
          range.deleteContents();
          range.insertNode(imgElement);
        }
      } catch (err) {
        console.error("Failed to insert block image range:", err);
        setHtmlContent(prev => prev + imgHtml);
      }
    } else {
      setHtmlContent(prev => prev + imgHtml);
    }
    // Force sync back to main HTML content
    setTimeout(() => {
      if (visualEditorRef.current) {
        setHtmlContent(visualEditorRef.current.innerHTML);
      }
    }, 80);
  };

  // Handle click inside visual editor to select image elements and display properties bar
  const handleVisualClick = (e) => {
    if (e.target.tagName === 'IMG') {
      const img = e.target;
      setSelectedImg(img);
      img.focus(); // Force browser selection outline focus
      
      const rect = img.getBoundingClientRect();
      const parent = visualEditorRef.current?.offsetParent || document.body;
      const parentRect = parent.getBoundingClientRect();
      
      // Prevent horizontal overflow in split screen by clamping 'left' boundaries
      const panelWidth = visualEditorRef.current?.clientWidth || 400;
      const calculatedLeft = rect.left - parentRect.left + (rect.width / 2) - 175; // 175 is half of ~350px toolbar width
      const safeLeft = Math.max(10, Math.min(panelWidth - 380, calculatedLeft));
      
      setImgToolbarPos({
        top: Math.max(10, rect.top - parentRect.top - 60),
        left: safeLeft
      });
    } else {
      setSelectedImg(null);
    }
  };

  // Modify image CSS rules dynamically from toolbar
  const updateImgStyle = (property, value) => {
    if (!selectedImg) return;
    
    if (property === 'align') {
      if (value === 'left') {
        selectedImg.style.display = 'inline';
        selectedImg.style.float = 'left';
        selectedImg.style.margin = '0 20px 20px 0';
      } else if (value === 'right') {
        selectedImg.style.display = 'inline';
        selectedImg.style.float = 'right';
        selectedImg.style.margin = '0 0 20px 20px';
      } else {
        selectedImg.style.display = 'block';
        selectedImg.style.float = 'none';
        selectedImg.style.margin = '16px auto';
      }
    } else if (property === 'width') {
      selectedImg.style.width = value;
      selectedImg.style.height = 'auto';
    } else if (property === 'radius') {
      selectedImg.style.borderRadius = value;
    }
    
    // Sync state
    if (visualEditorRef.current) {
      setHtmlContent(visualEditorRef.current.innerHTML);
    }
    
    // Refresh toolbar position
    setTimeout(() => {
      if (selectedImg && visualEditorRef.current) {
        const rect = selectedImg.getBoundingClientRect();
        const parent = visualEditorRef.current.offsetParent || document.body;
        const parentRect = parent.getBoundingClientRect();
        
        const panelWidth = visualEditorRef.current.clientWidth || 400;
        const calculatedLeft = rect.left - parentRect.left + (rect.width / 2) - 175;
        const safeLeft = Math.max(10, Math.min(panelWidth - 380, calculatedLeft));
        
        setImgToolbarPos({
          top: Math.max(10, rect.top - parentRect.top - 60),
          left: safeLeft
        });
      }
    }, 60);
  };

  // Delete image element node
  const deleteImg = () => {
    if (!selectedImg) return;
    selectedImg.remove();
    setSelectedImg(null);
    if (visualEditorRef.current) {
      setHtmlContent(visualEditorRef.current.innerHTML);
    }
  };

  // AI SEO Optimization
  const handleOptimizeSeo = async () => {
    if (!htmlContent.trim()) {
      toast.error("Please enter or import content before running SEO optimizer.");
      return;
    }
    setIsOptimizingSeo(true);
    try {
      // 1. Convert any embedded Base64 images to permanent S3 URLs first
      let currentContent = htmlContent;
      if (currentContent && currentContent.includes('data:image/')) {
        currentContent = await processBase64Images(currentContent);
        setHtmlContent(currentContent);
      }

      const payload = {
        title: title || 'Untitled Blog',
        htmlContent: currentContent,
        metaTitle,
        metaDescription,
        keywords,
        industry: industry || 'General'
      };
      const res = await axios.post(`${API_BASE_URL}/api/blogs/optimize-seo`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setSeoReport(res.data.data);
        setShowSeoModal(true);
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "SEO Optimization failed. Please check AI credentials.");
    } finally {
      setIsOptimizingSeo(false);
    }
  };

  const escapeHtml = (text) => {
    return text
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  };

  const applySeoOptimizations = () => {
    if (!seoReport) return;

    const optimizedContent = seoReport.optimizedHtmlContent;

    // Prepare escaped text for raw HTML highlight preview
    let tempHighlighted = escapeHtml(optimizedContent);

    if (Array.isArray(seoReport.changes)) {
      seoReport.changes.forEach(change => {
        const optSnippet = change.optimizedSnippet || change.optimized || change.newSnippet;
        if (optSnippet) {
          const escapedSnippet = escapeHtml(optSnippet);
          const highlightStart = `<span class="raw-html-highlight-${change.type}">`;
          const highlightEnd = `</span>`;
          tempHighlighted = tempHighlighted.replaceAll(
            escapedSnippet,
            `${highlightStart}${escapedSnippet}${highlightEnd}`
          );
        }
      });
    }

    setHighlightedHtml(tempHighlighted);
    setIsHighlightingChanges(true);
    setLayoutMode('split'); // Ensure split layout to show the highlighted HTML side-by-side

    // Extract optimized titles, slugs, and descriptions with rich fallback mappings
    const newTitle = seoReport.optimizedTitle || seoReport.title || seoReport.optimizedMetaTitle || title;
    const newSlug = seoReport.optimizedSlug || seoReport.slug || (newTitle ? newTitle.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") : slug);
    const newMetaTitle = seoReport.optimizedMetaTitle || seoReport.metaTitle || newTitle;
    const newMetaDesc = seoReport.optimizedMetaDescription || seoReport.metaDescription || seoReport.description || metaDescription;

    // Apply values including optimized Title and optimized Slug
    setTitle(newTitle);
    setSlug(newSlug);
    setMetaTitle(newMetaTitle);
    setMetaDescription(newMetaDesc);

    if (Array.isArray(seoReport.optimizedKeywords)) {
      setKeywords(seoReport.optimizedKeywords.join(', '));
    }
    setHtmlContent(optimizedContent);
    setShowSeoModal(false);
    toast.success("AI SEO Optimizations applied!");
  };

  const getSnippet = () => {
    const externalHost = API_BASE_URL || "https://api.toxbox.com";
    const key = apiKey || "YOUR_GENERATED_API_KEY";
    switch (selectedLang) {
      case 'curl':
        return `curl -X GET "${externalHost}/api/blogs/external?page=1&limit=10" \\\n  -H "X-toxbox-api-key: ${key}"`;
      case 'node':
        return `const axios = require('axios');\naxios.get('${externalHost}/api/blogs/external?slug=my-blog', {\n  headers: { 'X-toxbox-api-key': '${key}' }\n}).then(res => console.log(res.data));`;
      case 'python':
        return `import requests\nheaders = {'X-toxbox-api-key': '${key}'}\nres = requests.get('${externalHost}/api/blogs/external?page=1', headers=headers)\nprint(res.json())`;
      default:
        return '';
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <h2>Blog Management</h2>
        </div>
        {view === 'dashboard' ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button className={styles.secondaryBtn} onClick={() => setShowIntegrationModal(true)} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FaCode /> API Integration
            </button>
            <button className={styles.primaryBtn} onClick={handleCreateNew} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FaPlus /> Create Blog
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <button className={styles.secondaryBtn} onClick={() => setView('dashboard')}>
              Cancel
            </button>

            {htmlContent.trim() && (
              <>
                <button
                  type="button"
                  className={styles.secondaryBtn}
                  onClick={() => fileInputRef.current?.click()}
                  title="Import different HTML or Text document"
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', height: '40px' }}
                >
                  <FaFileUpload /> Re-Import Doc
                </button>

                <button
                  type="button"
                  onClick={handleOptimizeSeo}
                  disabled={isOptimizingSeo}
                  className={styles.primaryBtn}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    height: '40px',
                    background: 'linear-gradient(135deg, #a78bfa 0%, #7c3aed 100%)',
                    boxShadow: '0 4px 6px -1px rgba(124, 58, 237, 0.1)'
                  }}
                >
                  {isOptimizingSeo ? (
                    <><FaSpinner className={styles.spinAnimation} /> Analyzing...</>
                  ) : (
                    <><FaMagic /> Optimize SEO with AI</>
                  )}
                </button>

                <button className={styles.secondaryBtn} onClick={() => handleSave('draft')} disabled={isSaving} style={{ height: '40px', fontWeight: '600' }}>
                  {isSaving === 'draft' ? 'Saving...' : 'Save Draft'}
                </button>

                <button className={styles.secondaryBtn} onClick={() => setShowScheduleConfirmModal(true)} disabled={isSaving} style={{ height: '40px', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', fontWeight: '600' }}>
                  Schedule
                </button>

                <button className={styles.primaryBtn} onClick={() => setShowPublishConfirmModal(true)} disabled={isSaving} style={{ height: '40px', background: '#10b981', boxShadow: '0 4px 10px rgba(16, 185, 129, 0.2)', fontWeight: '600' }}>
                  Publish Now
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {view === 'dashboard' && (
        <>
          <div className={styles.grid}>
            {blogs.map(blog => (
              <div key={blog._id} className={`${styles.card} ${blog.status === 'draft' ? styles.draftCard : ''}`}>
                {blog.thumbnailUrl && <img src={blog.thumbnailUrl} alt="Thumbnail" className={styles.cardImage} />}
                <div className={styles.cardBody}>
                  <div className={styles.cardBadges}>
                    <span className={`${styles.badge} ${styles[blog.status]}`}>{blog.status}</span>
                    <span className={styles.badgeIndustry}>{blog.industry}</span>
                  </div>
                  <h3>{blog.title}</h3>
                  <small>Slug: {blog.slug}</small>
                  {blog.status === 'scheduled' && (
                    <div className={styles.scheduledAlert}>
                      Scheduled for: {new Date(blog.publishedAt).toLocaleString()}
                    </div>
                  )}
                  <div className={styles.cardActions}>
                    <button onClick={() => setPreviewBlog(blog)} className={styles.previewBtn} title="Preview Blog">
                      <FaEye />
                    </button>
                    <button onClick={() => handleEdit(blog)} className={styles.editBtn} title="Edit Blog">Edit</button>
                    <button onClick={() => handleDelete(blog._id)} className={styles.deleteBtn} title="Delete Blog"><FaTrash /></button>
                  </div>
                </div>
              </div>
            ))}
            {blogs.length === 0 && <p>No blogs found. Create one to get started!</p>}
          </div>
        </>
      )}

      {view === 'brief' && (
        <div className={styles.briefWorkspace}>
          {/* Header tabs */}
          <div className={styles.briefTabHeader}>
            <div className={styles.briefTabButtons}>
              <button
                type="button"
                onClick={() => {
                  setActiveBriefTab('brief');
                  setGeneratedBrief(null);
                }}
                className={`${styles.briefTabBtn} ${activeBriefTab === 'brief' ? styles.activeBriefTab : ''}`}
              >
                <FaMagic className={styles.tabIcon} /> Generate SEO Brief
              </button>
              <button
                type="button"
                onClick={() => setActiveBriefTab('import')}
                className={`${styles.briefTabBtn} ${activeBriefTab === 'import' ? styles.activeBriefTab : ''}`}
              >
                <FaCloudUploadAlt className={styles.tabIcon} /> Import HTML Document
              </button>
            </div>
          </div>

          {activeBriefTab === 'import' ? (
            <div className={styles.briefImportContainer}>
              <input
                type="file"
                accept=".html,.txt"
                style={{ display: 'none' }}
                ref={fileInputRef}
                onChange={handleFileUpload}
              />
              <div className={styles.uploadPlaceholderWrapper}>
                {isImporting ? (
                  <div className={styles.importingOverlay}>
                    <FaSpinner className={styles.spinnerLarge} />
                    <h3>Importing Document...</h3>
                    <p>{[
                      "Reading document contents...",
                      "Extracting HTML headers & structure...",
                      "Analyzing body formatting & tags...",
                      "Compiling layout parameters...",
                      "Writing SEO drafts...",
                      "Almost ready..."
                    ][importingStep]}</p>
                  </div>
                ) : (
                  <div
                    className={styles.uploadPlaceholder}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <div className={styles.uploadIconContainer}>
                      <FaCloudUploadAlt className={styles.uploadIcon} />
                    </div>
                    <h3>Import HTML or Text Document</h3>
                    <p>Drag and drop your file here, or click to browse</p>
                    <span className={styles.uploadBadge}>Supports .html, .txt</span>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className={`${styles.briefContentContainer} ${generatedBrief ? styles.splitView : styles.singleView}`}>
              {/* LEFT SIDE: Inputs / Prompt Panel */}
              <div className={styles.briefInputPanel}>
                <div className={styles.briefCardHeader}>
                  <h3><FaBrain className={styles.briefHeaderIcon} /> Configure SEO Target Parameters</h3>
                  <p>Define your topic description and optionally tweak SEO metrics below.</p>
                </div>

                <div className={styles.briefFormGroup}>
                  <label className={styles.briefLabel}>
                    Topic / Prompt Description <span className={styles.reqBadge}>Mandatory</span>
                    <span className={styles.tooltipIcon} data-tooltip="Describe the core topic of your article. You can write a single title, or enter a full detailed paragraph prompt specifying parameters (e.g. word count, language) directly in this box, just like in ChatGPT.">ⓘ</span>
                  </label>
                  <textarea
                    value={briefTopic}
                    onChange={e => setBriefTopic(e.target.value)}
                    placeholder="Tell us what you want to write about. You can write a simple topic, or define all parameters directly in this box.&#10;&#10;E.g.: 'Write a 1200-word article in Hinglish targeting B2B Sales Managers about how ToxBox AI automation helps with outbound cold calling. Include keywords: lead generation, voice assistant, cold calling...'"
                    rows={6}
                    className={styles.briefTextarea}
                  />
                </div>

                {/* Collapsible Advanced Parameters */}
                <div 
                  className={`${styles.advancedToggleHeader} ${isAdvancedOpen ? styles.toggleOpen : ''}`} 
                  onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
                >
                  <FaSlidersH className={styles.toggleIcon} />
                  <span>{isAdvancedOpen ? 'Hide Advanced Options' : 'Show Advanced Options (Optional)'}</span>
                </div>

                {isAdvancedOpen && (
                  <div className={styles.advancedFieldsGrid}>
                    <div className={styles.briefFormGroup}>
                      <label className={styles.briefLabel}>
                        Target Audience <span className={styles.optBadge}>Optional</span>
                        <span className={styles.tooltipIcon} data-tooltip="Who the article is written for. AI will refine the tone (casual, professional, technical) and explain concepts with relatable industry examples. Use case: Target 'First-time homebuyers' to keep it simple.">ⓘ</span>
                      </label>
                      <input
                        type="text"
                        value={briefTargetAudience}
                        onChange={e => setBriefTargetAudience(e.target.value)}
                        placeholder="e.g., Sales Managers, CTOs, Homebuyers"
                        className={styles.briefInput}
                      />
                    </div>

                    <div className={styles.briefFormGroup}>
                      <label className={styles.briefLabel}>
                        Language <span className={styles.optBadge}>Optional</span>
                        <span className={styles.tooltipIcon} data-tooltip="The output language of the brief, outline, and final article (e.g., English, Hindi, Hinglish, Marathi, Spanish).">ⓘ</span>
                      </label>
                      <select 
                        value={briefLanguage} 
                        onChange={e => setBriefLanguage(e.target.value)}
                        className={styles.briefSelect}
                      >
                        <option value="English">English</option>
                        <option value="Hindi">Hindi (हिंदी)</option>
                        <option value="Hinglish">Hinglish (Mix)</option>
                        <option value="Marathi">Marathi (मराठी)</option>
                        <option value="Spanish">Spanish (Español)</option>
                        <option value="French">French (Français)</option>
                        <option value="German">German (Deutsch)</option>
                      </select>
                    </div>

                    <div className={styles.briefFormGroup}>
                      <label className={styles.briefLabel}>
                        Target Word Count <span className={styles.optBadge}>Optional</span>
                        <span className={styles.tooltipIcon} data-tooltip="Total length target for the article. AI will structure headings, detail depth, and list segments based on this count. Use case: Enter '800' for a quick post, or '2500' for a guide.">ⓘ</span>
                      </label>
                      <input
                        type="number"
                        value={briefWordCount}
                        onChange={e => setBriefWordCount(e.target.value)}
                        placeholder="e.g., 1200 (Default: Auto-detect)"
                        className={styles.briefInput}
                      />
                    </div>

                    <div className={styles.briefFormGroup}>
                      <label className={styles.briefLabel}>
                        Mandatory Keywords <span className={styles.optBadge}>Optional</span>
                        <span className={styles.tooltipIcon} data-tooltip="Specific keywords that must be integrated in the SEO outline. AI will weave them into headings and body keywords suggestions. Use case: Force 'ToxBox AI' for brand rankings.">ⓘ</span>
                      </label>
                      <input
                        type="text"
                        value={briefMandatoryKeywords}
                        onChange={e => setBriefMandatoryKeywords(e.target.value)}
                        placeholder="e.g., ToxBox AI, outbound agent, sales automation"
                        className={styles.briefInput}
                      />
                    </div>
                  </div>
                )}

                <button
                  type="button"
                  onClick={handleGenerateBrief}
                  disabled={isGeneratingBrief}
                  className={styles.briefActionBtnPrimary}
                >
                  {isGeneratingBrief ? (
                    <>
                      <FaSpinner className={styles.spinner} /> Generating Brief Outline...
                    </>
                  ) : (
                    generatedBrief ? "🔄 Re-Generate Brief" : "✨ Generate SEO Brief"
                  )}
                </button>
              </div>

              {/* RIGHT SIDE: Generated Output Report */}
              {generatedBrief && (
                <div className={styles.briefResultPanel}>
                  {isGeneratingBrief && (
                    <div className={styles.briefResultLoadingOverlay}>
                      <FaSpinner className={`${styles.spinner} ${styles.spinnerLarge}`} />
                      <p style={{ margin: 0, fontWeight: '700', color: '#4f46e5' }}>Updating Brief Outline...</p>
                    </div>
                  )}
                  <div className={styles.briefCardHeader}>
                    <h3>📋 Generated SEO Content Brief Outline</h3>
                    <p>Double-check headings and keywords. Click "Write with AI" to generate full content.</p>
                  </div>

                  <div className={styles.briefReportContent}>
                    {/* Suggested Title */}
                    <div className={styles.briefReportSection}>
                      <div className={styles.badgePremium}>AI Suggested Title</div>
                      <h2 className={styles.reportBlogTitle}>{generatedBrief.suggestedTitle}</h2>
                    </div>

                    {/* Word Count / Read time / Angle */}
                    <div className={styles.reportMetadataGrid}>
                      <div className={styles.metaBadgeItem}>
                        <span className={styles.metaBadgeLabel}>Target Word Count</span>
                        <span className={styles.metaBadgeValue}>{generatedBrief.recommendedWordCount} words</span>
                      </div>
                      <div className={styles.metaBadgeItem}>
                        <span className={styles.metaBadgeLabel}>Estimated Read Time</span>
                        <span className={styles.metaBadgeValue}>{generatedBrief.estimatedReadTime}</span>
                      </div>
                    </div>

                    {generatedBrief.contentAngle && (
                      <div className={styles.briefReportSection}>
                        <label className={styles.reportSectionLabel}>Narrative Tone & Angle</label>
                        <p className={styles.reportTextAngle}>{generatedBrief.contentAngle}</p>
                      </div>
                    )}

                    {/* Google SERP Card */}
                    <div className={styles.briefReportSection}>
                      <label className={styles.reportSectionLabel}>Google SERP Snippet Preview</label>
                      <div className={styles.serpPreviewBox}>
                        <span className={styles.serpUrl}>https://yoursite.com/blog/{(generatedBrief.suggestedTitle || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}</span>
                        <h4 className={styles.serpTitle}>{generatedBrief.metaTitleSuggestion}</h4>
                        <p className={styles.serpDesc}>{generatedBrief.metaDescriptionSuggestion}</p>
                      </div>
                    </div>

                    {/* Keywords pills */}
                    <div className={styles.briefReportSection}>
                      <label className={styles.reportSectionLabel}>Suggested Target SEO Keywords</label>
                      <div className={styles.reportKeywordsRow}>
                        <span className={styles.keywordPillPrimary}>🔑 {generatedBrief.primaryKeyword}</span>
                        {generatedBrief.secondaryKeywords?.map((kw, i) => (
                          <span key={i} className={styles.keywordPillSecondary}>{kw}</span>
                        ))}
                      </div>
                    </div>

                    {/* Heading outline tree */}
                    <div className={styles.briefReportSection}>
                      <label className={styles.reportSectionLabel}>Heading Structure Outline</label>
                      <div className={styles.reportOutlineTree}>
                        <div className={styles.treeH1}>H1: {generatedBrief.outline?.h1 || generatedBrief.suggestedTitle}</div>
                        {generatedBrief.outline?.sections?.map((sec, secIdx) => (
                          <div key={secIdx} className={styles.treeH2Group}>
                            <div className={styles.treeH2}>H2: {sec.h2}</div>
                            {sec.sectionGuide && (
                              <div className={styles.treeGuideText}>
                                💡 <strong>Writing Tip:</strong> {sec.sectionGuide}
                              </div>
                            )}
                            {sec.h3s?.map((h3, h3idx) => (
                              <div key={h3idx} className={styles.treeH3}>H3: {h3}</div>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Split view bottom action bar */}
                  <div className={styles.briefResultActions}>
                    <button
                      type="button"
                      onClick={() => handleWriteFromBrief('ai')}
                      disabled={isWritingWithAI}
                      className={styles.briefActionBtnPrimary}
                    >
                      {isWritingWithAI ? (
                        <>
                          <FaSpinner className={styles.spinner} /> Writing Article...
                        </>
                      ) : (
                        <>
                          <FaMagic /> Generate Blog with AI
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleWriteFromBrief('manual')}
                      disabled={isWritingWithAI}
                      className={styles.briefActionBtnSecondary}
                    >
                      ✍️ Write Manually
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {view === 'editor' && (
        <div className={styles.editorLayout}>
          <div className={styles.editorMain} style={{ width: '100%' }}>
            <input
              type="file"
              accept=".html,.txt"
              style={{ display: 'none' }}
              ref={fileInputRef}
              onChange={handleFileUpload}
            />

            {!htmlContent.trim() ? (
              <div className={styles.uploadPlaceholderWrapper}>
                <div
                  className={styles.uploadPlaceholder}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <div className={styles.uploadIconContainer}>
                    <FaCloudUploadAlt className={styles.uploadIcon} />
                  </div>
                  <h3>Import HTML or Text Document</h3>
                  <p>Drag and drop your file here, or click to browse</p>
                  <span className={styles.uploadBadge}>Supports .html, .txt</span>
                </div>
              </div>
            ) : (
              <>
                {/* Side-by-side Split View Workspace */}
                <div className={styles.editorAreaSplit}>
                  {/* Left Column: Visual Preview */}
                  {(layoutMode === 'split' || layoutMode === 'full-visual') && (
                    <div className={`${styles.splitPanel} ${layoutMode === 'full-visual' ? styles.fullPanel : ''}`}>
                      <div className={styles.panelHeader}>
                        <span>Visual Preview (WYSIWYG)</span>
                        <button
                          type="button"
                          onClick={() => setLayoutMode(layoutMode === 'full-visual' ? 'split' : 'full-visual')}
                          className={styles.panelToggleBtn}
                        >
                          {layoutMode === 'full-visual' ? "Split View" : "Full Screen"}
                        </button>
                      </div>
                      <div
                        className={styles.visualEditor}
                        contentEditable={true}
                        ref={visualEditorRef}
                        onInput={handleVisualEdit}
                        onMouseUp={handleVisualSelection}
                        onKeyUp={handleVisualSelection}
                        onDrop={handleVisualDrop}
                        onDragOver={(e) => e.preventDefault()}
                        onPaste={handleVisualPaste}
                        onClick={handleVisualClick}
                      />
                      {selectedImg && (
                        <div 
                          className={styles.imgFloatingToolbar} 
                          style={{ top: `${imgToolbarPos.top}px`, left: `${imgToolbarPos.left}px` }}
                        >
                          <div className={styles.toolbarGroup}>
                            <span className={styles.toolbarLabel}>Align:</span>
                            <button type="button" className={styles.toolbarBtn} onClick={() => updateImgStyle('align', 'left')}>⬅ Left</button>
                            <button type="button" className={styles.toolbarBtn} onClick={() => updateImgStyle('align', 'center')}>⬆ Center</button>
                            <button type="button" className={styles.toolbarBtn} onClick={() => updateImgStyle('align', 'right')}>➡ Right</button>
                          </div>
                          
                          <div className={styles.toolbarDivider} />
                          
                          <div className={styles.toolbarGroup}>
                            <span className={styles.toolbarLabel}>Width:</span>
                            <button type="button" className={styles.toolbarBtn} onClick={() => updateImgStyle('width', '25%')}>25%</button>
                            <button type="button" className={styles.toolbarBtn} onClick={() => updateImgStyle('width', '50%')}>50%</button>
                            <button type="button" className={styles.toolbarBtn} onClick={() => updateImgStyle('width', '75%')}>75%</button>
                            <button type="button" className={styles.toolbarBtn} onClick={() => updateImgStyle('width', '100%')}>100%</button>
                          </div>
                          
                          <div className={styles.toolbarDivider} />
                          
                          <div className={styles.toolbarGroup}>
                            <span className={styles.toolbarLabel}>Corners:</span>
                            <button type="button" className={styles.toolbarBtn} onClick={() => updateImgStyle('radius', '0px')}>Sharp</button>
                            <button type="button" className={styles.toolbarBtn} onClick={() => updateImgStyle('radius', '8px')}>Round</button>
                            <button type="button" className={styles.toolbarBtn} onClick={() => updateImgStyle('radius', '20px')}>Pill</button>
                          </div>
                          
                          <div className={styles.toolbarDivider} />
                          
                          <button type="button" className={styles.toolbarDeleteBtn} onClick={deleteImg} title="Delete Image">
                            <FaTrash />
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Right Column: Raw HTML Editor / Highlight changes */}
                  {(layoutMode === 'split' || layoutMode === 'full-html') && (
                    <div className={`${styles.splitPanel} ${layoutMode === 'full-html' ? styles.fullPanel : ''}`}>
                      <div className={styles.panelHeader}>
                        <span>Raw HTML Editor {isHighlightingChanges && "(AI Optimization Highlights)"}</span>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          {isHighlightingChanges && (
                            <button
                              type="button"
                              onClick={() => setIsHighlightingChanges(false)}
                              className={styles.panelToggleBtn}
                              style={{ backgroundColor: '#fee2e2', color: '#ef4444', borderColor: '#fecaca', marginRight: '8px' }}
                            >
                              Edit Raw HTML
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => setLayoutMode(layoutMode === 'full-html' ? 'split' : 'full-html')}
                            className={styles.panelToggleBtn}
                          >
                            {layoutMode === 'full-html' ? "Split View" : "Full Screen"}
                          </button>
                        </div>
                      </div>

                      <div className={styles.textareaWrapper}>
                        {isHighlightingChanges ? (
                          <pre
                            className={styles.htmlHighlightView}
                            dangerouslySetInnerHTML={{ __html: highlightedHtml }}
                          />
                        ) : (
                          <textarea
                            ref={textareaRef}
                            className={styles.htmlTextarea}
                            value={htmlContent}
                            onChange={(e) => setHtmlContent(e.target.value)}
                            placeholder="Paste or write your HTML here..."
                          />
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* importing Loader overlay */}
      {isImporting && createPortal(
        <div className={styles.modalOverlay}>
          <div className={styles.importingCard}>
            <div className={styles.blogLoaderIcon}>
              <FaFileUpload className={styles.spinAnimation} style={{ color: '#6366f1', fontSize: '32px' }} />
            </div>
            <h3>Importing Your Blog Document</h3>
            <div className={styles.progressBarContainer}>
              <div className={styles.progressBar} style={{ width: `${(importingStep + 1) * 16.6}%` }} />
            </div>
            <p className={styles.importingStepText}>
              {["Reading document contents...", "Extracting HTML headers & structure...", "Analyzing body formatting & tags...", "Compiling layout parameters...", "Writing SEO drafts...", "Almost ready..."][importingStep]}
            </p>
          </div>
        </div>,
        document.body
      )}

      {/* SEO Modal (rendered in body via portal) */}
      {showSeoModal && seoReport && createPortal(
        <div className={styles.modalOverlay} onClick={() => setShowSeoModal(false)}>
          <div className={styles.seoModalContent} onClick={e => e.stopPropagation()}>
            <div className={styles.seoHeader}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <FaMagic style={{ color: '#8b5cf6', fontSize: '24px' }} />
                <h3>AI SEO Audit Report</h3>
              </div>
              <button className={styles.closeBtn} onClick={() => setShowSeoModal(false)}>&times;</button>
            </div>

            <div className={styles.seoScoreSection}>
              <div className={styles.seoGauge}>
                <div className={styles.seoGaugeCircle} style={{ '--score': seoReport.seoScore }}>
                  <span>{seoReport.seoScore}</span>
                  <small>Overall</small>
                </div>
              </div>

              <div className={styles.seoBreakdownDashboard}>
                <h4>SEO Score Breakdown</h4>
                {seoReport.scoreBreakdown ? (
                  <div className={styles.breakdownGrid}>
                    <div className={styles.breakdownItem}>
                      <div className={styles.breakdownHeader}>
                        <span>Heading Hierarchy</span>
                        <strong>{seoReport.scoreBreakdown.headers || 0}/100</strong>
                      </div>
                      <div className={styles.barContainer}>
                        <div className={styles.barFill} style={{ width: `${seoReport.scoreBreakdown.headers || 0}%`, background: '#8b5cf6' }} />
                      </div>
                    </div>

                    <div className={styles.breakdownItem}>
                      <div className={styles.breakdownHeader}>
                        <span>Image Alt Tagging</span>
                        <strong>{seoReport.scoreBreakdown.images || 0}/100</strong>
                      </div>
                      <div className={styles.barContainer}>
                        <div className={styles.barFill} style={{ width: `${seoReport.scoreBreakdown.images || 0}%`, background: '#3b82f6' }} />
                      </div>
                    </div>

                    <div className={styles.breakdownItem}>
                      <div className={styles.breakdownHeader}>
                        <span>Keyword Placements</span>
                        <strong>{seoReport.scoreBreakdown.keywords || 0}/100</strong>
                      </div>
                      <div className={styles.barContainer}>
                        <div className={styles.barFill} style={{ width: `${seoReport.scoreBreakdown.keywords || 0}%`, background: '#10b981' }} />
                      </div>
                    </div>

                    <div className={styles.breakdownItem}>
                      <div className={styles.breakdownHeader}>
                        <span>Readability Flow</span>
                        <strong>{seoReport.scoreBreakdown.readability || 0}/100</strong>
                      </div>
                      <div className={styles.barContainer}>
                        <div className={styles.barFill} style={{ width: `${seoReport.scoreBreakdown.readability || 0}%`, background: '#f59e0b' }} />
                      </div>
                    </div>

                    <div className={styles.breakdownItem}>
                      <div className={styles.breakdownHeader}>
                        <span>Metadata Formatting</span>
                        <strong>{seoReport.scoreBreakdown.meta || 0}/100</strong>
                      </div>
                      <div className={styles.barContainer}>
                        <div className={styles.barFill} style={{ width: `${seoReport.scoreBreakdown.meta || 0}%`, background: '#ec4899' }} />
                      </div>
                    </div>
                  </div>
                ) : (
                  <p>Check the list below for specific optimization summaries.</p>
                )}
              </div>
            </div>

            <div className={styles.seoDetailsGrid}>
              <div className={styles.seoSection}>
                <h4>Optimizations Applied</h4>
                <ul className={styles.seoReportList}>
                  {seoReport.report?.map((item, i) => (
                    <li key={i}><FaCheckCircle className={styles.successIcon} /> {item}</li>
                  ))}
                </ul>
              </div>

              <div className={styles.seoSection}>
                <h4>Changes Highlighted</h4>
                <div className={styles.seoDiffContainer}>
                  {seoReport.changes?.map((change, i) => {
                    const origText = change.originalSnippet || change.original || change.oldSnippet || "";
                    const optText = change.optimizedSnippet || change.optimized || change.newSnippet || "";
                    return (
                      <div key={i} className={styles.seoDiffItem}>
                        <div className={styles.diffHeader}>
                          <span className={`${styles.diffType} ${styles[change.type]}`}>{change.type}</span>
                          <p>{change.description}</p>
                        </div>
                        {origText && (
                          <div className={styles.diffOriginal}>
                            <small className={styles.diffLabel}>Original Code/Content:</small>
                            <pre>{origText}</pre>
                          </div>
                        )}
                        {optText && (
                          <div className={styles.diffOptimized}>
                            <small className={styles.diffLabel}>Optimized Code/Content:</small>
                            <pre>{optText}</pre>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className={styles.seoModalActions}>
              <button onClick={() => setShowSeoModal(false)} className={styles.secondaryBtn}>Cancel</button>
              <button onClick={applySeoOptimizations} className={styles.primaryBtn}>
                <FaCheck /> Apply Changes
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Publish Now Confirm & Settings Modal (rendered in body via portal) */}
      {showPublishConfirmModal && createPortal(
        <div className={styles.modalOverlay} onClick={() => setShowPublishConfirmModal(false)}>
          <div className={styles.modalContentLarge} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Publish Settings & Metadata</h3>
              <button className={styles.closeBtn} onClick={() => setShowPublishConfirmModal(false)}>&times;</button>
            </div>

            <div className={styles.modalBodyScrollable}>
              <div className={styles.formGrid}>
                <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                  <label>Blog Title *</label>
                  <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="E.g., Future of AI in B2B Customer Support" />
                </div>
                <div className={styles.formGroup}>
                  <label>Slug (URL path)</label>
                  <input type="text" value={slug} onChange={e => setSlug(e.target.value)} placeholder="Leave blank to auto-generate" />
                </div>
                <div className={styles.formGroup}>
                  <label>Industry</label>
                  {!isCustomIndustry && availableIndustries.length > 0 ? (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <select
                        value={industry}
                        onChange={e => setIndustry(e.target.value)}
                        style={{ flexGrow: 1, padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                      >
                        <option value="">Select an Industry...</option>
                        {availableIndustries.map(ind => (
                          <option key={ind} value={ind}>{ind}</option>
                        ))}
                      </select>
                      <button type="button" onClick={() => { setIsCustomIndustry(true); setIndustry(''); }} className={styles.secondaryBtn} style={{ padding: '0 12px' }}>
                        + New
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input
                        type="text"
                        value={industry}
                        onChange={e => setIndustry(e.target.value)}
                        placeholder="E.g., Healthcare or Finance"
                        style={{ flexGrow: 1 }}
                      />
                      {availableIndustries.length > 0 && (
                        <button type="button" onClick={() => { setIsCustomIndustry(false); setIndustry(availableIndustries[0] || ''); }} className={styles.secondaryBtn} style={{ padding: '0 12px' }}>
                          Cancel
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                  <label>Thumbnail</label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="text"
                      value={thumbnailUrl}
                      onChange={e => setThumbnailUrl(e.target.value)}
                      placeholder="Paste image URL (https://...) or choose a file"
                      style={{ flexGrow: 1 }}
                    />
                    <label className={styles.secondaryBtn} style={{ cursor: 'pointer', padding: '0 16px', display: 'flex', alignItems: 'center', height: '40px', border: '1px solid #cbd5e1', borderRadius: '8px', gap: '6px', whiteSpace: 'nowrap', margin: 0 }}>
                      <FaCloudUploadAlt size={16} /> Upload Image
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleThumbnailUpload}
                        style={{ display: 'none' }}
                      />
                    </label>
                  </div>
                  {thumbnailUrl && (
                    <div style={{ marginTop: '12px', display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '10px', background: '#f8fafc', borderRadius: '8px', border: '1px dashed #cbd5e1', width: 'fit-content' }}>
                      <img
                        src={thumbnailUrl}
                        alt="Thumbnail Preview"
                        style={{ maxWidth: '160px', maxHeight: '100px', borderRadius: '6px', objectFit: 'cover', display: 'block' }}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://placehold.co/160x100?text=Invalid+Image+URL';
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setThumbnailUrl('')}
                        style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', padding: '2px 6px', borderRadius: '4px', transition: 'background 0.2s' }}
                        onMouseOver={(e) => e.target.style.background = '#fee2e2'}
                        onMouseOut={(e) => e.target.style.background = 'transparent'}
                      >
                        <FaTrash size={12} /> Clear
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '20px 0' }} />
              <h4 style={{ margin: '0 0 12px 0', color: '#1e293b', fontSize: '14px', fontWeight: '700' }}>Search Engine Optimization (SEO)</h4>

              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label>Meta Title</label>
                  <input type="text" value={metaTitle} onChange={e => setMetaTitle(e.target.value)} placeholder="SEO Meta Title" />
                </div>
                <div className={styles.formGroup}>
                  <label>Keywords (comma separated)</label>
                  <input type="text" value={keywords} onChange={e => setKeywords(e.target.value)} placeholder="SEO, marketing, blogs" />
                </div>
                <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                  <label>Meta Description</label>
                  <textarea value={metaDescription} onChange={e => setMetaDescription(e.target.value)} placeholder="SEO description..." style={{ minHeight: '60px' }} />
                </div>
              </div>
            </div>

            <div className={styles.modalActions} style={{ marginTop: '20px', paddingTop: '12px', borderTop: '1px solid #e2e8f0' }}>
              <button onClick={() => setShowPublishConfirmModal(false)} className={styles.secondaryBtn}>Cancel</button>
              <button
                onClick={() => {
                  if (!title.trim()) {
                    toast.error("Please enter a Blog Title.");
                    return;
                  }
                  setShowPublishConfirmModal(false);
                  handleSave('published');
                }}
                className={styles.primaryBtn}
                style={{ background: '#10b981' }}
              >
                Confirm & Publish
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Schedule Confirm & Settings Modal (rendered in body via portal) */}
      {showScheduleConfirmModal && createPortal(
        <div className={styles.modalOverlay} onClick={() => setShowScheduleConfirmModal(false)}>
          <div className={styles.modalContentLarge} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Schedule Settings & Metadata</h3>
              <button className={styles.closeBtn} onClick={() => setShowScheduleConfirmModal(false)}>&times;</button>
            </div>

            <div className={styles.modalBodyScrollable}>
              <div className={styles.formGrid}>
                <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                  <label style={{ color: '#4f46e5', fontWeight: '800' }}>Publish Date/Time *</label>
                  <input
                    type="datetime-local"
                    value={publishedAt}
                    onChange={e => setPublishedAt(e.target.value)}
                    style={{ marginTop: '6px', width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #6366f1', fontWeight: '600' }}
                  />
                </div>
              </div>

              <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '20px 0' }} />

              <div className={styles.formGrid}>
                <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                  <label>Blog Title *</label>
                  <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="E.g., Future of AI in B2B Customer Support" />
                </div>
                <div className={styles.formGroup}>
                  <label>Slug (URL path)</label>
                  <input type="text" value={slug} onChange={e => setSlug(e.target.value)} placeholder="Leave blank to auto-generate" />
                </div>
                <div className={styles.formGroup}>
                  <label>Industry</label>
                  {!isCustomIndustry && availableIndustries.length > 0 ? (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <select
                        value={industry}
                        onChange={e => setIndustry(e.target.value)}
                        style={{ flexGrow: 1, padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                      >
                        <option value="">Select an Industry...</option>
                        {availableIndustries.map(ind => (
                          <option key={ind} value={ind}>{ind}</option>
                        ))}
                      </select>
                      <button type="button" onClick={() => { setIsCustomIndustry(true); setIndustry(''); }} className={styles.secondaryBtn} style={{ padding: '0 12px' }}>
                        + New
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input
                        type="text"
                        value={industry}
                        onChange={e => setIndustry(e.target.value)}
                        placeholder="E.g., Healthcare or Finance"
                        style={{ flexGrow: 1 }}
                      />
                      {availableIndustries.length > 0 && (
                        <button type="button" onClick={() => { setIsCustomIndustry(false); setIndustry(availableIndustries[0] || ''); }} className={styles.secondaryBtn} style={{ padding: '0 12px' }}>
                          Cancel
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                  <label>Thumbnail</label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="text"
                      value={thumbnailUrl}
                      onChange={e => setThumbnailUrl(e.target.value)}
                      placeholder="Paste image URL (https://...) or choose a file"
                      style={{ flexGrow: 1 }}
                    />
                    <label className={styles.secondaryBtn} style={{ cursor: 'pointer', padding: '0 16px', display: 'flex', alignItems: 'center', height: '40px', border: '1px solid #cbd5e1', borderRadius: '8px', gap: '6px', whiteSpace: 'nowrap', margin: 0 }}>
                      <FaCloudUploadAlt size={16} /> Upload Image
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleThumbnailUpload}
                        style={{ display: 'none' }}
                      />
                    </label>
                  </div>
                  {thumbnailUrl && (
                    <div style={{ marginTop: '12px', display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '10px', background: '#f8fafc', borderRadius: '8px', border: '1px dashed #cbd5e1', width: 'fit-content' }}>
                      <img
                        src={thumbnailUrl}
                        alt="Thumbnail Preview"
                        style={{ maxWidth: '160px', maxHeight: '100px', borderRadius: '6px', objectFit: 'cover', display: 'block' }}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://placehold.co/160x100?text=Invalid+Image+URL';
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setThumbnailUrl('')}
                        style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', padding: '2px 6px', borderRadius: '4px', transition: 'background 0.2s' }}
                        onMouseOver={(e) => e.target.style.background = '#fee2e2'}
                        onMouseOut={(e) => e.target.style.background = 'transparent'}
                      >
                        <FaTrash size={12} /> Clear
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '20px 0' }} />
              <h4 style={{ margin: '0 0 12px 0', color: '#1e293b', fontSize: '14px', fontWeight: '700' }}>Search Engine Optimization (SEO)</h4>

              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label>Meta Title</label>
                  <input type="text" value={metaTitle} onChange={e => setMetaTitle(e.target.value)} placeholder="SEO Meta Title" />
                </div>
                <div className={styles.formGroup}>
                  <label>Keywords (comma separated)</label>
                  <input type="text" value={keywords} onChange={e => setKeywords(e.target.value)} placeholder="SEO, marketing, blogs" />
                </div>
                <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                  <label>Meta Description</label>
                  <textarea value={metaDescription} onChange={e => setMetaDescription(e.target.value)} placeholder="SEO description..." style={{ minHeight: '60px' }} />
                </div>
              </div>
            </div>

            <div className={styles.modalActions} style={{ marginTop: '20px', paddingTop: '12px', borderTop: '1px solid #e2e8f0' }}>
              <button onClick={() => setShowScheduleConfirmModal(false)} className={styles.secondaryBtn}>Cancel</button>
              <button
                onClick={() => {
                  if (!publishedAt) {
                    toast.error("Please select a date and time to schedule.");
                    return;
                  }
                  if (!title.trim()) {
                    toast.error("Please enter a Blog Title.");
                    return;
                  }
                  setShowScheduleConfirmModal(false);
                  handleSave('scheduled');
                }}
                className={styles.primaryBtn}
                style={{ background: '#3b82f6' }}
              >
                Confirm Schedule
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* API Integration Modal */}
      {showIntegrationModal && createPortal(
        <div className={styles.modalOverlay} onClick={() => setShowIntegrationModal(false)}>
          <div className={styles.modalContentLarge} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>External API Integration</h3>
              <button className={styles.closeBtn} onClick={() => setShowIntegrationModal(false)}>&times;</button>
            </div>
            <div className={styles.modalBodyScrollable}>
              <p style={{ color: '#64748b', fontSize: '13px', margin: '0 0 16px 0' }}>
                Use your secure API key to display these blogs on your own website or platform.
              </p>

              <div className={styles.formGroup}>
                <label>Language Selector</label>
                <select value={selectedLang} onChange={e => setSelectedLang(e.target.value)} className={styles.selectInput} style={{ width: '100%', marginBottom: '16px' }}>
                  <option value="curl">cURL</option>
                  <option value="node">Node.js</option>
                  <option value="python">Python</option>
                </select>
              </div>

              {(() => {
                const expiryStatus = getApiKeyExpiryStatus();
                return apiKey ? (
                  <div className={expiryStatus?.isExpired ? styles.apiKeyAlertExpired : styles.apiKeyAlert} style={{ marginBottom: '16px' }}>
                    <div className={styles.apiKeyHeader}>
                      <strong style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {expiryStatus?.isExpired ? (
                          <>
                            <FaTimesCircle style={{ color: '#dc2626' }} />
                            <span>Blog API Key (Expired)</span>
                          </>
                        ) : (
                          <>
                            <FaCheckCircle style={{ color: '#16a34a' }} />
                            <span>Active Blog API Key Assigned</span>
                          </>
                        )}
                      </strong>

                      {expiryStatus && (
                        <span className={`${styles.expiryBadge} ${
                          expiryStatus.status === 'lifetime'
                            ? styles.expiryBadgeLifetime
                            : expiryStatus.status === 'expired'
                            ? styles.expiryBadgeExpired
                            : styles.expiryBadgeExpiring
                        }`}>
                          {expiryStatus.status === 'lifetime' && <FaShieldAlt />}
                          {expiryStatus.status === 'active' && <FaClock />}
                          {expiryStatus.status === 'expired' && <FaExclamationTriangle />}
                          {expiryStatus.label}
                        </span>
                      )}
                    </div>

                    {expiryStatus?.isExpired && (
                      <div style={{
                        fontSize: '12px',
                        color: '#991b1b',
                        backgroundColor: '#fee2e2',
                        border: '1px solid #fca5a5',
                        borderRadius: '6px',
                        padding: '8px 12px',
                        marginBottom: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <FaExclamationTriangle style={{ flexShrink: 0 }} />
                        <span><strong>API Key Expired:</strong> External requests to <code>/api/blogs/external</code> will be rejected. Generate a new lifetime or extended key below.</span>
                      </div>
                    )}

                    <div className={styles.apiSecretContainer}>
                      <input
                        type="text"
                        value={apiKey}
                        readOnly
                        className={styles.apiSecretInput}
                        style={{
                          borderColor: expiryStatus?.isExpired ? '#fca5a5' : '#cbd5e1',
                          color: expiryStatus?.isExpired ? '#991b1b' : '#1e293b'
                        }}
                      />
                      <button className={styles.iconBtn} onClick={handleCopyApiKey} title="Copy Key">
                        {copiedApiKey ? <FaCheck style={{ color: '#10b981' }} /> : <FaCopy />}
                      </button>
                    </div>

                    {/* Regenerate Section */}
                    <div className={styles.regenerateSection}>
                      {!showRegenerateForm ? (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '12px', color: '#64748b' }}>
                            Need a fresh key or want to extend expiration?
                          </span>
                          <button
                            type="button"
                            onClick={() => setShowRegenerateForm(true)}
                            className={styles.secondaryBtn}
                            style={{
                              padding: '6px 14px',
                              fontSize: '12px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              background: '#ffffff',
                              border: '1px solid #cbd5e1'
                            }}
                          >
                            <FaSyncAlt /> Regenerate Key
                          </button>
                        </div>
                      ) : (
                        <div style={{
                          background: '#ffffff',
                          border: '1px solid #e2e8f0',
                          borderRadius: '6px',
                          padding: '12px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '10px'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '12.5px', fontWeight: 600, color: '#334155' }}>
                              Generate New API Key
                            </span>
                            <button
                              type="button"
                              onClick={() => setShowRegenerateForm(false)}
                              style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '16px' }}
                            >
                              &times;
                            </button>
                          </div>

                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <label style={{ fontSize: '12px', color: '#475569', fontWeight: 500 }}>
                              Key Expiry:
                            </label>
                            <select
                              value={keyExpiryDays}
                              onChange={e => setKeyExpiryDays(e.target.value)}
                              className={styles.selectInput}
                              style={{
                                fontSize: '12.5px',
                                padding: '6px 10px',
                                borderRadius: '6px',
                                border: '1px solid #cbd5e1',
                                flexGrow: 1,
                                maxWidth: '260px'
                              }}
                            >
                              <option value="never">Never Expires (Lifetime - Recommended)</option>
                              <option value="30">30 Days</option>
                              <option value="90">90 Days</option>
                              <option value="365">1 Year (365 Days)</option>
                            </select>

                            <button
                              type="button"
                              disabled={isRegeneratingKey}
                              onClick={() => handleGenerateApiKey(keyExpiryDays)}
                              className={styles.primaryBtn}
                              style={{
                                padding: '6px 16px',
                                fontSize: '12.5px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                height: '34px'
                              }}
                            >
                              {isRegeneratingKey ? (
                                <>
                                  <FaSpinner className={styles.spin} /> Generating...
                                </>
                              ) : (
                                <>
                                  <FaKey /> Generate Key
                                </>
                              )}
                            </button>
                          </div>
                          <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                            * Note: Generating a new key replaces the current key. Remember to update <code>TOXBOX_BLOG_API_KEY</code> in your connected backend's <code>.env</code> file.
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className={styles.noApiKeyWarning} style={{ marginBottom: '16px', flexDirection: 'column', alignItems: 'flex-start', gap: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <FaExclamationTriangle />
                      <span>No Blog API key assigned yet.</span>
                    </div>
                    <button
                      type="button"
                      disabled={isRegeneratingKey}
                      onClick={() => handleGenerateApiKey('never')}
                      className={styles.primaryBtn}
                      style={{ padding: '6px 16px', fontSize: '12.5px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                    >
                      {isRegeneratingKey ? (
                        <>
                          <FaSpinner className={styles.spin} /> Generating...
                        </>
                      ) : (
                        <>
                          <FaKey /> Generate Lifetime API Key
                        </>
                      )}
                    </button>
                  </div>
                );
              })()}

              <div className={styles.formGroup}>
                <label>Code Snippet</label>
                <div className={styles.codeSnippet} style={{ marginTop: '6px' }}>
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{getSnippet()}</pre>
                  <button className={styles.copyBtn} onClick={handleCopySnippet} style={{ background: copiedSnippet ? '#064e3b' : 'rgba(255,255,255,0.1)', color: copiedSnippet ? '#34d399' : 'white', border: copiedSnippet ? '1px solid #059669' : '1px solid rgba(255,255,255,0.15)' }}>
                    {copiedSnippet ? <><FaCheck style={{ marginRight: '6px' }} /> Copied!</> : 'Copy Snippet'}
                  </button>
                </div>
              </div>
            </div>
            <div className={styles.modalActions} style={{ marginTop: '20px', paddingTop: '12px', borderTop: '1px solid #e2e8f0' }}>
              <button onClick={() => setShowIntegrationModal(false)} className={styles.secondaryBtn}>Close</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {previewBlog && createPortal(
        <div className={styles.modalOverlay} onClick={() => setPreviewBlog(null)}>
          <div className={styles.previewModal} onClick={e => e.stopPropagation()}>
            <button className={styles.closePreviewBtn} onClick={() => setPreviewBlog(null)}>&times;</button>
            <div className={styles.previewModalHeader}>
              <h2>{previewBlog.title}</h2>
              <span className={styles.badgeIndustry}>{previewBlog.industry || 'General'}</span>
            </div>
            <div className={styles.previewModalContent} dangerouslySetInnerHTML={{ __html: previewBlog.htmlContent }} />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
